/**
 * El descarte de recepción (visita 5/9, r. 9–13 y 68), lo carga quien lo lleva al monte. Desde la
 * v6 (docs/esquema.md §12.8, Nicolás 2/10) el descarte tiene sus carros propios con tara: se anotan
 * los carros y el BRUTO de la balanza, y el neto lo calcula la app (y lo escribe el servidor), como
 * en el viaje. Cuatro pantallas —¿quién?, carros, bruto, confirmar—.
 */

import { BorradorYaConfirmado, encolar, type MutacionAEncolar } from '../datos/cola';
import { taraAplicable } from '../nucleo/tara';
import { fechaDeProduccion, horaCorta } from '../nucleo/tiempo';
import { armarPayloadDescarte, esCarroDeVerde, netoDelDescarteG, nombreDeCarroDeDescarte, terminoDeTipearElDescarte, type BorradorDescarte, type CarroDeDescarteElegido } from '../nucleo/viaje';
import { botonMantener, colorSeguro, el, flag, icono, kg, mil, padAnclado, PALETA, teclado, tile } from './componentes';
import { acciones, ahoraMs, app, conPersonaHabitual, descartarBorrador, guardarBorrador, irA, quienesLlevanElDescarte } from './estado';
import { campaniaAbierta, grillaPersonas, nombreDe, pregunta, reloj } from './pantallas-captura';

/** Cota sanitaria de UI: un descarte de más de 20 t no es un dedazo, es otra cosa. */
const MAX_DESCARTE_KG = 20_000;

const comprometiendo = new Set<string>();

async function irAlPaso(borrador: BorradorDescarte, paso: number): Promise<void> {
	borrador.paso = paso;
	await guardarBorrador(borrador).catch((error) => console.error('no se pudo guardar el borrador', error));
	app.render();
}

function pie(pieNodo: HTMLElement, borrador: BorradorDescarte, derecho?: { texto: string; deshabilitado?: boolean; alTocar: () => void }): void {
	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = async () => {
		if (borrador.paso > 0) {
			await irAlPaso(borrador, borrador.paso - 1);
			return;
		}
		// Desde la primera pantalla, volver es volver al viaje: el borrador de
		// descarte recién nace y no se pierde nada.
		await descartarBorrador(borrador).catch(() => null);
		acciones.nuevaCaptura();
	};
	pieNodo.appendChild(volver);
	if (derecho) {
		const boton = el('button', { class: 'btn go', type: 'button' }, derecho.texto);
		if (derecho.deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = derecho.alTocar;
		pieNodo.appendChild(boton);
	}
}

export function pintarDescarte(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: BorradorDescarte): void {
	const puntos = el('div', { class: 'dots' });
	for (let i = 0; i < 4; i++) {
		const punto = el('i');
		if (borrador.paso >= i) punto.className = 'on';
		puntos.appendChild(punto);
	}
	cuerpo.appendChild(puntos);
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);
	contenido.appendChild(flag('ok', 'Descarte al monte.', 'No es un viaje: es lo que se va.'));

	switch (borrador.paso) {
		case 0:
			return pQuien(contenido, pieNodo, borrador);
		case 1:
			return pCarros(contenido, pieNodo, borrador);
		case 2:
			return pKilos(contenido, pieNodo, borrador);
		default:
			return pRevisar(contenido, pieNodo, borrador);
	}
}

function pQuien(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: BorradorDescarte): void {
	// 0004 (§12.13): lo llevan una o dos personas, las que gestión marcó (hoy Darío y Carlos); la
	// grilla admite dos como el viaje —el dueño del teléfono no se suelta y la selección nunca queda
	// vacía—. Con las marcadas elegidas tal cual, el copy dice que ya están; si no, el de siempre.
	const llevan = quienesLlevanElDescarte();
	const marcadas = llevan.length > 0 && llevan.length === borrador.personas.length && llevan.every((id) => borrador.personas.includes(id));
	pregunta(
		cuerpo,
		'¿Quién lleva el descarte?',
		marcadas
			? llevan.length === 1
				? 'Lo lleva siempre la misma persona y ya está marcada. Si hoy va otro, tocá su nombre y sacá al que no va; si van de a dos, tocá al otro.'
				: 'Lo llevan siempre las mismas personas y ya están marcadas. Si hoy va uno solo, sacá al otro; si va otra persona, sacá a uno y tocá su nombre.'
			: conPersonaHabitual(borrador.personas)
				? 'Ya estás vos. Si lo lleva otro, tocá su nombre; si van de a dos, tocá los dos.'
				: 'Tocá tu nombre. Si van de a dos, tocá los dos.',
	);
	cuerpo.appendChild(
		grillaPersonas(borrador.personas, 2, () => {
			void guardarBorrador(borrador);
			app.render();
		}),
	);
	pie(pieNodo, borrador, { texto: 'Siguiente', deshabilitado: borrador.personas.length === 0, alTocar: () => void irAlPaso(borrador, 1) });
}

/**
 * v6 (§12.8): los carros, con su tara de la fecha de la pesada (congelada al tocarlos). Primero los de
 * descarte y, aparte, los de verde, que a veces también van (2/10): dos listas con numeración propia.
 */
function pCarros(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: BorradorDescarte): void {
	borrador.registrado_ms ??= ahoraMs();
	borrador.carros ??= [];
	pregunta(cuerpo, '¿En qué carros va?', 'Tocá todos los que van a la balanza: los de descarte y, si fue alguno, los de verde.');
	const fecha = fechaDeProduccion(borrador.registrado_ms);
	const tarasDescarte = (app.catalogos?.carros_descarte_tara ?? []).map((t) => ({ id: t.id, carro_id: t.carro_descarte_id, tara_g: t.tara_g, vigente_desde: t.vigente_desde }));
	const deDescarte = (app.catalogos?.carros_descarte ?? []).filter((c) => c.activo === 1).sort((a, b) => a.numero - b.numero);
	const deVerde = (app.catalogos?.carros ?? []).filter((c) => c.activo === 1).sort((a, b) => a.numero - b.numero);
	if (deDescarte.length === 0) cuerpo.appendChild(flag('warn', 'Todavía no hay carros de descarte cargados.', 'Avisale a la oficina: se dan de alta en Administrar, con su tara. Mientras, se pueden usar los de verde.'));

	const grillaDe = (titulo: string, lista: Array<{ id: string; numero: number; color: string | null }>, verde: boolean, colorBase: number) => {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, titulo));
		const grilla = el('div', { class: 'grid2' });
		lista.forEach((carro, indice) => {
			const tara = taraAplicable(verde ? (app.catalogos?.carros_tara ?? []) : tarasDescarte, carro.id, fecha);
			const esEste = (c: CarroDeDescarteElegido) => (verde ? esCarroDeVerde(c) && c.carro_id === carro.id : !esCarroDeVerde(c) && c.carro_descarte_id === carro.id);
			const elegido = borrador.carros?.find(esEste);
			grilla.appendChild(
				tile({
					etiqueta: `Carro ${carro.numero}`,
					sub: tara ? `vacío ${kg(tara.tara_g)} kg` : 'sin tara — avisale a la oficina',
					color: colorSeguro(carro.color, PALETA[(indice + colorBase) % PALETA.length]),
					avatarTexto: String(carro.numero),
					avatarCuadrado: true,
					seleccionado: Boolean(elegido),
					deshabilitado: !tara,
					alTocar: () => {
						if (!tara || !borrador.carros) return;
						if (elegido) borrador.carros = borrador.carros.filter((c) => !esEste(c));
						else {
							borrador.carros.push(
								verde
									? { carro_id: carro.id, carro_tara_id: tara.id, tara_g_snapshot: tara.tara_g, numero: carro.numero, color: carro.color }
									: { carro_descarte_id: carro.id, carro_descarte_tara_id: tara.id, tara_g_snapshot: tara.tara_g, numero: carro.numero, color: carro.color },
							);
							// Los de descarte primero, después los de verde, cada lista por número.
							borrador.carros.sort((a, b) => Number(esCarroDeVerde(a)) - Number(esCarroDeVerde(b)) || a.numero - b.numero);
						}
						void guardarBorrador(borrador);
						app.render();
					},
				}),
			);
		});
		cuerpo.appendChild(grilla);
	};
	if (deDescarte.length > 0) grillaDe('Carros de descarte', deDescarte, false, 3);
	if (deVerde.length > 0) grillaDe('Carros de verde', deVerde, true, 0);
	const elegidos = borrador.carros.length;
	if (elegidos > 0) {
		const tarasG = borrador.carros.reduce((suma, c) => suma + c.tara_g_snapshot, 0);
		cuerpo.appendChild(flag('ok', `${elegidos} carro${elegidos > 1 ? 's' : ''} · vacíos ${kg(tarasG)} kg`, '— Se resta solo cuando cargues el peso.'));
	}
	pie(pieNodo, borrador, { texto: 'Siguiente', deshabilitado: elegidos === 0, alTocar: () => void irAlPaso(borrador, 2) });
}

function pKilos(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: BorradorDescarte): void {
	borrador.registrado_ms ??= ahoraMs();
	// Pad anclado como en el resto: el reloj de la pesada va en la franja del medio, y en
	// un teléfono bajo se llega a él con el scroll de la zona (no con la copia vieja del pad).
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	pregunta(arriba, '¿Cuánto marcó la balanza?', 'Con los carros: el bruto. Los carros vacíos se restan solos.');
	const tipeado = borrador.bruto_kg ?? null;
	const neto = netoDelDescarteG(borrador);

	const lector = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', tipeado ? undefined : { class: 'apagado' }, tipeado ? mil(tipeado) : '0'));
	grande.appendChild(el('span', { class: 'unit' }, 'kg'));
	lector.appendChild(grande);
	const renglon = el('div', { class: 'barlab' });
	renglon.appendChild(el('span', undefined, 'neto, sin los carros'));
	renglon.appendChild(el('b', undefined, neto === null || !tipeado ? '—' : `${kg(Math.max(0, neto))} kg`));
	lector.appendChild(renglon);
	arriba.appendChild(lector);

	const pad =
		teclado(
			(digito) => {
				const actual = String(tipeado ?? '');
				if (actual.length >= 5) return;
				borrador.bruto_kg = Number(`${actual}${digito}`);
				void guardarBorrador(borrador);
				app.render();
			},
			() => {
				const actual = String(tipeado ?? '');
				borrador.bruto_kg = actual.length > 1 ? Number(actual.slice(0, -1)) : null;
				void guardarBorrador(borrador);
				app.render();
			},
		);

	const demasiado = tipeado !== null && tipeado > MAX_DESCARTE_KG;
	// El bruto tiene que superar a los carros vacíos: si no, el neto da cero o menos. El botón se
	// traba siempre; el cartel espera a que el número tenga las cifras de los carros (2/10).
	const noSupera = tipeado !== null && neto !== null && neto <= 0;
	if (demasiado) {
		medio.appendChild(flag('stop', 'Ese número no puede ser.', `Un descarte no pasa de ${mil(MAX_DESCARTE_KG)} kg. Fijate la balanza.`));
	} else if (noSupera && terminoDeTipearElDescarte(borrador)) {
		medio.appendChild(flag('stop', 'Es menos que los carros vacíos.', 'El bruto incluye los carros: fijate la balanza o qué carros marcaste.'));
	}

	const registradoMs = borrador.registrado_ms;
	reloj(medio, registradoMs, 'hora de la pesada', (nuevo) => {
		borrador.registrado_ms = nuevo;
		void guardarBorrador(borrador);
		app.render();
	});
	cuerpo.appendChild(pad);

	pie(pieNodo, borrador, {
		texto: 'Siguiente',
		deshabilitado: !tipeado || demasiado || noSupera,
		alTocar: () => void irAlPaso(borrador, 3),
	});
}

function pRevisar(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: BorradorDescarte): void {
	pregunta(cuerpo, '¿Está todo bien?', 'Mirá el número antes de guardar.');
	const neto = netoDelDescarteG(borrador);
	const filas: Array<[Parameters<typeof icono>[0], string, string, number]> = [
		['persona', 'Quién', borrador.personas.map(nombreDe).join(' y '), 0],
		// 7/10: era 'silo', que no existe en el mapa de íconos, e `icono()` tiraba: la revisión quedaba en
		// blanco y no se podía confirmar un descarte desde el 2/10. Ahora el tsc lo rechaza.
		['carro', 'Carros', (borrador.carros ?? []).map(nombreDeCarroDeDescarte).join(' + ') || '—', 1],
		['peso', 'Descarte', `${neto === null ? '—' : kg(neto)} kg netos · ${mil(borrador.bruto_kg ?? 0)} kg brutos`, 2],
		['reloj', 'Pesado a las', borrador.registrado_ms ? horaCorta(borrador.registrado_ms) : '—', 2],
	];
	const resumen = el('div', { class: 'sum' });
	for (const [nombreIcono, etiqueta, valor, paso] of filas) {
		const fila = el('div', { class: 'r' });
		const cajaIcono = el('div', { class: 'ic' });
		cajaIcono.appendChild(icono(nombreIcono, 19));
		fila.appendChild(cajaIcono);
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, etiqueta));
		textos.appendChild(el('div', { class: 'vl' }, valor));
		fila.appendChild(textos);
		const cambiar = el('button', { class: 'fix', type: 'button' }, 'cambiar');
		cambiar.onclick = () => void irAlPaso(borrador, paso);
		fila.appendChild(cambiar);
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);

	if (borrador.nota === null) {
		const agregar = el('button', { class: 'enlace', type: 'button' }, 'agregar una nota');
		agregar.onclick = () => {
			borrador.nota = '';
			app.render();
		};
		cuerpo.appendChild(agregar);
	} else {
		const nota = el('textarea', { class: 'nota', rows: '2', maxlength: '400', placeholder: 'Nota para la oficina…' });
		nota.value = borrador.nota;
		nota.oninput = () => {
			borrador.nota = nota.value;
			void guardarBorrador(borrador);
		};
		cuerpo.appendChild(nota);
	}

	pie(pieNodo, borrador);
	pieNodo.appendChild(botonMantener('Mantené apretado', 1500, () => void confirmarDescarte(borrador)));
}

async function confirmarDescarte(borrador: BorradorDescarte): Promise<void> {
	if (comprometiendo.has(borrador.capture_id)) return;
	comprometiendo.add(borrador.capture_id);
	try {
		const campania = borrador.campania_id ?? campaniaAbierta();
		const payload = armarPayloadDescarte(borrador, campania);
		const mutacion: MutacionAEncolar = {
			id: borrador.mutacion_id,
			tipo: 'descarte.crear',
			payload,
			entidad: 'descarte',
			registro_id: borrador.descarte_id,
			personas: [...borrador.personas],
			// La hora declarada, para que «Verde, últimas 24 horas» ordene y recorte lo propio (§5.5).
			resumen: { entidad: 'descarte', fecha: fechaDeProduccion(borrador.registrado_ms ?? ahoraMs()), registrado_en: payload.registrado_en, neto_g: netoDelDescarteG(borrador) ?? 0 },
		};
		await encolar(app.bd, [mutacion], [borrador.capture_id], new Date().toISOString());
		irA({
			tipo: 'guardado',
			entidad: 'descarte',
			registroId: borrador.descarte_id,
			netoG: netoDelDescarteG(borrador) ?? 0,
			nroViaje: null,
			enviado: false,
		});
		app.dispararSync();
	} catch (error) {
		if (error instanceof BorradorYaConfirmado) {
			await descartarBorrador(borrador).catch(() => null);
			alert('Ese descarte ya se mandó desde otra pestaña: no se mandó dos veces.');
			acciones.nuevaCaptura();
			return;
		}
		console.error('no se pudo guardar el descarte', error);
		alert('No se guardó. Revisá y probá de nuevo; si sigue fallando, avisale al encargado.');
		app.render();
	} finally {
		comprometiendo.delete(borrador.capture_id);
	}
}
